/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentType } from 'react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { token } from '@atlaskit/tokens';

import { RovoSettingsModal } from '@atlassian/rovo-settings-modal/ui/rovo-settings-modal';

import {
	CONNECTOR_OAUTH_STATUS,
	type ConnectorOAuthStatus,
} from '@confluence/connectors/entry-points/connector-oauth-types.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { QualifyingProduct } from './thirdPartyPromptTypes.ts';
import { getCardStackComponent, shouldDrawIconOnStack } from './thirdPartyCardStackAssets.ts';
import { thirdPartyPromptCardMessages as i18n } from './thirdPartyPromptCardMessages.ts';
import { CARD_STACK_ICON_SIZE, PROMPT_CARD_ICON_SIZE } from './thirdPartyPromptCardDimensions.ts';

const CONNECT_MORE_APPS_SURFACE = 'confluence-related-content-connections';

const APPS_TAB_ID = 'discovery';

type OpenConnectorModal = (options?: { defaultTab?: string }) => void;

const RegisterOpenModal = ({
	open,
	onRegister,
}: {
	open: OpenConnectorModal;
	onRegister: (open: OpenConnectorModal | null) => void;
}) => {
	useEffect(() => {
		onRegister(open);

		return () => onRegister(null);
	}, [open, onRegister]);

	return null;
};

// Style values must be local literals: `ui-styling-standard/no-imported-style-values` rejects
// imported constants in styles, including the `style` prop.
const CARD_HEIGHT = 68;
const STACK_WIDTH = 333;

// How far the clip reaches into the card, hiding the artwork's card feet behind it (incl. ~8px bleed).
const STACK_OVERLAP = 48;

const STACK_ASPECT = '333 / 135';
const STACK_ICON_TOP = '20.74%';
const STACK_ICON_LEFT = '30.48%';
const STACK_ICON_WIDTH = '7.21%';
const STACK_ICON_HEIGHT = '17.78%';

// The artwork slides up from behind the card on hover and focus. State and an inline transform drive
// it, because `cssMap` types reject a custom property inside a pseudo block.
const styles = cssMap({
	wrapper: {
		position: 'relative',
		width: '100%',
	},
	// The window the artwork slides through. Clipping keeps it out of sight at rest.
	stackClip: {
		position: 'absolute',
		bottom: `calc(100% - ${STACK_OVERLAP}px)`,
		left: '50%',
		transform: 'translateX(-50%)',
		width: `${STACK_WIDTH}px`,
		maxWidth: '100%',
		aspectRatio: STACK_ASPECT,
		overflow: 'hidden',
		pointerEvents: 'none',
	},
	// Enter: fade in near the start of the slide up. Leave mirrors it via `stackLayerLowered`.
	stackLayerRaised: {
		position: 'relative',
		width: '100%',
		transition: `transform ${token('motion.duration.long')} ${token('motion.easing.out.practical')}, opacity ${token('motion.duration.xxshort')} ${token('motion.easing.out.practical')}`,
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	stackLayerLowered: {
		position: 'relative',
		width: '100%',
		// Hiding is a touch slower than the reveal. The delay holds the fade until the slide is nearly
		// done, so the slide down stays visible.
		transition: `transform ${token('motion.duration.xlong')} ${token('motion.easing.out.practical')}, opacity ${token('motion.duration.xxshort')} ${token('motion.easing.out.practical')} ${token('motion.duration.long')}`,
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	stackIcon: {
		position: 'absolute',
		top: STACK_ICON_TOP,
		left: STACK_ICON_LEFT,
		width: STACK_ICON_WIDTH,
		height: STACK_ICON_HEIGHT,
	},
	// Chrome and padding copied from `Card`/`StyledCard` in page-card, so the prompt matches its row.
	card: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		height: '100%',
		padding: token('space.100'),
		backgroundColor: token('elevation.surface'),
		borderColor: token('color.border'),
		borderStyle: 'dashed',
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		boxSizing: 'border-box',
		color: token('color.text'),
		textAlign: 'left',
		cursor: 'pointer',
		'&:hover': {
			boxShadow: token('elevation.shadow.overflow'),
		},
	},
	// Sizing comes from the img's width and height attributes (PROMPT_CARD_ICON_SIZE).
	icon: {
		flexShrink: 0,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	label: {
		overflow: 'hidden',
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 2,
	},
});

export type ThirdPartyRelatedContentPromoCardProps = {
	product: QualifyingProduct;
	/** The connect flow's state machine, owned by the consumer. Promise settlement is not an outcome. */
	status: ConnectorOAuthStatus;
	// Starts the connect flow. Fire and forget: `status` reports what happened.
	onConnect: () => void;
	// Set false when a parent already supplies the list-item role.
	showCardAsListitem?: boolean;
	testId?: string;
};

type Props = ThirdPartyRelatedContentPromoCardProps & WithFlagsProps;

const PromoCard = ({
	product,
	status,
	onConnect,
	showCardAsListitem = true,
	testId = 'third-party-related-content-promo-card',
	flags,
}: Props) => {
	const { formatMessage } = useIntl();
	const { cloudId } = useSessionData();
	const [isRaised, setIsRaised] = useState(false);
	const { appId, displayName, iconUrl } = product;
	const isConnecting = status === CONNECTOR_OAUTH_STATUS.CONNECTING;

	const raise = useCallback(() => setIsRaised(true), []);
	const lower = useCallback(() => setIsRaised(false), []);

	// The error flag's "Try again" reruns the flow. The ref lets the flag call the latest `onConnect`
	// without the effect depending on it.
	const connectRef = useRef(onConnect);
	connectRef.current = onConnect;

	const openModalRef = useRef<OpenConnectorModal | null>(null);

	const registerOpenModal = useCallback((open: OpenConnectorModal | null) => {
		openModalRef.current = open;
	}, []);

	const openUniversalConnectorModal = useCallback(() => {
		openModalRef.current?.({ defaultTab: APPS_TAB_ID });
	}, []);

	// The flags follow the state machine, as `create-space`'s ConnectorRow does. A cancelled window
	// returns the status to idle, so neither transition fires and the card stays as it was.
	const previousStatusRef = useRef<ConnectorOAuthStatus>(status);

	useEffect(() => {
		const wasConnecting = previousStatusRef.current === CONNECTOR_OAUTH_STATUS.CONNECTING;
		previousStatusRef.current = status;

		if (!wasConnecting) {
			return;
		}

		if (status === CONNECTOR_OAUTH_STATUS.CONNECTED) {
			void flags.showSuccessFlag({
				title: formatMessage(i18n.connectSuccessTitle, { appName: displayName }),
				description: formatMessage(i18n.connectSuccessDescription, { appName: displayName }),
				actions: [
					{
						content: formatMessage(i18n.connectSuccessConnectMoreApps),
						onClick: openUniversalConnectorModal,
					},
				],
			});
			return;
		}

		if (status === CONNECTOR_OAUTH_STATUS.ERROR) {
			void flags.showErrorFlag({
				title: formatMessage(i18n.connectErrorTitle, { appName: displayName }),
				description: formatMessage(i18n.connectErrorDescription, { appName: displayName }),
				actions: [
					{
						content: formatMessage(i18n.connectErrorTryAgain),
						onClick: () => connectRef.current(),
					},
				],
			});
		}
	}, [displayName, flags, formatMessage, openUniversalConnectorModal, status]);

	const CardStack = getCardStackComponent(appId);
	const label = formatMessage(i18n.connectCta, { appName: displayName });

	// Success replaces the prompt.
	const card =
		status === CONNECTOR_OAUTH_STATUS.CONNECTED ? null : (
			<div
				css={styles.wrapper}
				style={{ height: `${CARD_HEIGHT}px` }}
				role={showCardAsListitem ? 'listitem' : undefined}
				onMouseEnter={raise}
				onMouseLeave={lower}
				onFocus={raise}
				onBlur={lower}
				data-testid={`${testId}-wrapper`}
			>
				{/* A div, not a Box: xcss rejects absolute positioning, clipping, and transitions. */}
				<div css={styles.stackClip} aria-hidden data-testid={`${testId}-stack-clip`}>
					<div
						css={styles[isRaised ? 'stackLayerRaised' : 'stackLayerLowered']}
						style={{
							transform: `translateY(${isRaised ? '0%' : '100%'})`,
							opacity: isRaised ? 1 : 0,
						}}
						data-testid={`${testId}-stack-layer`}
					>
						<CardStack testId={`${testId}-stack`} />
						{shouldDrawIconOnStack(appId) && iconUrl && (
							<img
								css={styles.stackIcon}
								src={iconUrl}
								alt=""
								width={CARD_STACK_ICON_SIZE}
								height={CARD_STACK_ICON_SIZE}
								data-testid={`${testId}-stack-icon`}
							/>
						)}
					</div>
				</div>
				<button
					css={styles.card}
					type="button"
					onClick={onConnect}
					disabled={isConnecting}
					data-testid={testId}
				>
					<div css={styles.row}>
						{iconUrl ? (
							<img
								css={styles.icon}
								src={iconUrl}
								alt=""
								width={PROMPT_CARD_ICON_SIZE}
								height={PROMPT_CARD_ICON_SIZE}
								data-testid={`${testId}-icon`}
							/>
						) : (
							// Fallback when a product has no icon, matching Figma node 72:10938.
							<TeamworkGraphIcon
								label=""
								color={token('color.icon.subtle')}
								testId={`${testId}-icon-fallback`}
							/>
						)}
						<span css={styles.label}>{label}</span>
					</div>
				</button>
			</div>
		);

	// The host renders no markup of its own and loads the modal only on the first open, so keeping it
	// mounted past success costs a component, not a chunk.
	return (
		<RovoSettingsModal product="confluence" cloudId={cloudId} surface={CONNECT_MORE_APPS_SURFACE}>
			{({ open }) => (
				<Fragment>
					<RegisterOpenModal open={open} onRegister={registerOpenModal} />
					{card}
				</Fragment>
			)}
		</RovoSettingsModal>
	);
};

export const ThirdPartyRelatedContentPromoCard: ComponentType<ThirdPartyRelatedContentPromoCardProps> =
	withFlags<Props>(PromoCard);
